import classNames from 'classnames';
import { cloneElement, type ReactElement } from 'react';

import type { PricingConfiguratorProps } from './types';
import { Card, Heading, HelpIcon, Separator, Tooltip, TooltipProvider } from '../../atoms';
import type { ContactsTierSelectProps } from '../ContactsTierSelect';
import type { CurrencySelectProps } from '../CurrencySelect';
import type { NonProfitToggleProps } from '../NonProfitToggle';
import type { PrepaySelectProps } from '../PrepaySelect';

export interface PricingConfiguratorContainerProps {
  heading: PricingConfiguratorProps['heading'];
  nonProfitTooltip?: PricingConfiguratorProps['nonProfitTooltip'];
  prepaySelect: ReactElement<PrepaySelectProps> | null;
  contactsTierSelect: ReactElement<ContactsTierSelectProps> | null;
  smsBundleTierSelect?: ReactElement | null;
  nonProfitToggle: ReactElement<NonProfitToggleProps> | null;
  currencySelect: ReactElement<CurrencySelectProps> | null;
  className?: string;
}

export const PricingConfiguratorContainer = ({
  heading,
  nonProfitTooltip,
  prepaySelect,
  contactsTierSelect,
  smsBundleTierSelect,
  nonProfitToggle,
  currencySelect,
  className,
}: PricingConfiguratorContainerProps) => {
  const showSmsBundleTierRow = Boolean(contactsTierSelect && smsBundleTierSelect);
  const columnCount = Boolean((prepaySelect || nonProfitToggle) && contactsTierSelect) || showSmsBundleTierRow ? 2 : 1;
  const hasPrepayOrNonprofit = Boolean(prepaySelect || nonProfitToggle);

  if (!prepaySelect && !contactsTierSelect && !smsBundleTierSelect && !nonProfitToggle && !currencySelect) {
    return null;
  }

  return (
    <Card
      className={classNames('payflow-ui pricing-configurator', className, {
        'pricing-configurator--sms-bundle-tiers': showSmsBundleTierRow,
      })}
    >
      <div
        className={classNames(
          'pricing-configurator__content',
          columnCount === 2 ? 'pricing-configurator__content-two-columns' : 'pricing-configurator__content-one-column',
          showSmsBundleTierRow && 'pricing-configurator__content--sms-bundle-tiers'
        )}
      >
        {columnCount === 2 && (
          <>
            <div className="pricing-configurator__column-one">
              <div className="pricing-configurator__heading-and-currency">
                <div className="pricing-configurator__heading">
                  {heading && <Heading level="h6">{heading}</Heading>}
                  {nonProfitToggle && nonProfitTooltip && (
                    <div className="non-profit-toggle_tooltip">
                      <TooltipProvider>
                        <Tooltip content={nonProfitTooltip}>
                          <HelpIcon />
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
                <div className="pricing-configurator__mobile-currency-select">{currencySelect}</div>
              </div>
              {hasPrepayOrNonprofit && (
                <div
                  className={classNames('pricing-configurator__box', {
                    'pricing-configurator__box--one-control': !prepaySelect || !nonProfitToggle,
                  })}
                >
                  {prepaySelect}
                  {nonProfitToggle}
                </div>
              )}
            </div>
            <Separator orientation="vertical" className="pricing-configurator__separator" />
          </>
        )}
        <div
          className={classNames(
            columnCount === 2 ? 'pricing-configurator__column-two' : 'pricing-configurator__column-full',
            showSmsBundleTierRow && 'pricing-configurator__column-two--sms-bundle-tiers'
          )}
        >
          {currencySelect}
          {showSmsBundleTierRow && smsBundleTierSelect ? (
            <div className="pricing-configurator__sms-bundle-tiers-row">
              <div className="pricing-configurator__sms-bundle-tiers-row__col">{contactsTierSelect}</div>
              <div className="pricing-configurator__sms-bundle-tiers-row__col">
                {cloneElement(smsBundleTierSelect, { inConfiguratorRow: true })}
              </div>
            </div>
          ) : (
            <>
              {contactsTierSelect && <div className="pricing-configurator__box">{contactsTierSelect}</div>}
              {smsBundleTierSelect}
            </>
          )}
          {prepaySelect && columnCount === 1 && (
            <>
              {heading && <Heading level="h6">{heading}</Heading>}
              <div className="pricing-configurator__box pricing-configurator__box--fullwidth ">{prepaySelect}</div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
